import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { AlertTriangle, CalendarClock, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatTile, PageLoader, Progress } from "@/components/ui";
import { HEALTH_META, money, pct, fmtDate } from "@/lib/format";

export function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-portfolio"],
    queryFn: () => api.get<any>("/dashboard/portfolio"),
  });

  if (isLoading || !data) return <PageLoader />;
  const t = data.totals;

  const healthData = Object.entries(t.counts)
    .filter(([, v]) => (v as number) > 0)
    .map(([k, v]) => ({ name: HEALTH_META[k as keyof typeof HEALTH_META].label, value: v as number, color: HEALTH_META[k as keyof typeof HEALTH_META].color }));

  const budgetData = data.projects.map((p: any) => ({
    name: p.code || p.name.slice(0, 10),
    Planned: p.plannedBudget,
    Actual: p.actualCost,
  }));

  return (
    <>
      <PageHeader title="Portfolio Dashboard" subtitle={`Live roll-up across ${t.projects} active project${t.projects === 1 ? "" : "s"} · generated ${fmtDate(data.generatedAt)}`} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Overall progress" value={pct(t.overallPercent)} sub={`${t.activities} activities`} accent="rgb(var(--brand-600))" />
        <StatTile label="Planned budget" value={money(t.plannedBudget)} />
        <StatTile label="Actual spend" value={money(t.actualCost)} sub={`${pct(t.percentBudgetUsed)} of budget`} />
        <StatTile label="Budget variance" value={money(t.budgetVariance)}
          accent={t.budgetVariance < 0 ? "rgb(var(--st-delayed))" : "rgb(var(--st-ontrack))"} />
        <StatTile label="Delayed activities" value={t.counts.delayed}
          accent={t.counts.delayed > 0 ? "rgb(var(--st-delayed))" : undefined} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="card p-4">
          <h3 className="mb-2 text-sm font-semibold">Schedule health</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={healthData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {healthData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card p-4 lg:col-span-2">
          <h3 className="mb-2 text-sm font-semibold">Planned vs actual spend by project</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={budgetData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={70} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Planned" fill="rgb(var(--brand-300))" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Actual" fill="rgb(var(--brand-600))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-4 card overflow-hidden p-0">
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <TrendingUp size={16} className="text-brand-600" />
          <h3 className="text-sm font-semibold">Projects</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr>
                {["Project", "Owner", "Manager", "Progress", "Activities", "Planned", "Actual", "Variance", "Delayed"].map((h) => <th key={h} className="th">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.projects.map((p: any) => (
                <tr key={p.id} className="hover:bg-surface-2/50">
                  <td className="td font-medium"><Link to={`/projects/${p.id}`} className="hover:text-brand-600">{p.name}</Link></td>
                  <td className="td text-ink-soft">{p.ownerName || "—"}</td>
                  <td className="td text-ink-soft">{p.managerName || "—"}</td>
                  <td className="td w-40">
                    <div className="flex items-center gap-2">
                      <Progress value={p.overallPercent} className="w-20" />
                      <span className="text-xs tabular-nums">{pct(p.overallPercent)}</span>
                    </div>
                  </td>
                  <td className="td tabular-nums">{p.totalActivities}</td>
                  <td className="td tabular-nums">{money(p.plannedBudget, p.currency)}</td>
                  <td className="td tabular-nums">{money(p.actualCost, p.currency)}</td>
                  <td className="td tabular-nums" style={{ color: p.budgetVariance < 0 ? "rgb(var(--st-delayed))" : undefined }}>{money(p.budgetVariance, p.currency)}</td>
                  <td className="td tabular-nums">{p.counts.delayed > 0 ? <span className="chip bg-st-delayed/10 text-st-delayed">{p.counts.delayed}</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="card p-0">
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <AlertTriangle size={15} className="text-st-delayed" />
            <h3 className="text-sm font-semibold">Delayed activities</h3>
          </div>
          <div className="divide-y divide-line">
            {data.delayed.length === 0 && <p className="px-4 py-6 text-sm text-ink-faint">Nothing delayed. 🎉</p>}
            {data.delayed.map((a: any, i: number) => (
              <Link key={i} to={`/projects/${a.projectId}/schedule`} className="flex items-center justify-between px-4 py-2.5 hover:bg-surface-2/60">
                <span className="min-w-0"><span className="block truncate text-sm font-medium">{a.title}</span>
                  <span className="text-xs text-ink-faint">{a.projectName}{a.responsible ? ` · ${a.responsible}` : ""}</span></span>
                <span className="shrink-0 text-right text-xs"><span className="block text-st-delayed">due {fmtDate(a.endDate)}</span><span className="text-ink-faint">{pct(a.percentComplete)}</span></span>
              </Link>
            ))}
          </div>
        </div>
        <div className="card p-0">
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <CalendarClock size={15} className="text-brand-600" />
            <h3 className="text-sm font-semibold">Due in the next 2 weeks</h3>
          </div>
          <div className="divide-y divide-line">
            {data.upcoming.length === 0 && <p className="px-4 py-6 text-sm text-ink-faint">Nothing due soon.</p>}
            {data.upcoming.map((a: any, i: number) => (
              <Link key={i} to={`/projects/${a.projectId}/schedule`} className="flex items-center justify-between px-4 py-2.5 hover:bg-surface-2/60">
                <span className="min-w-0"><span className="block truncate text-sm font-medium">{a.title}</span>
                  <span className="text-xs text-ink-faint">{a.projectName}</span></span>
                <span className="shrink-0 text-right text-xs"><span className="block">{fmtDate(a.endDate)}</span><span className="text-ink-faint">{pct(a.percentComplete)}</span></span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
